import FsLightbox from 'fslightbox-react'
import { useEffect, useState } from 'react'
import { apiUrl } from '../../../../config/api'
import { customSwal } from '../../../../config/swal'

export interface CarouselItem {
  id: number
  name: string
  visible: boolean
  image_url: string
  order: number
}

interface Props {
  images: CarouselItem[]
  onSave?: (data: CarouselItem[]) => void
  isLoading?: boolean
}

export const ImageCarouselCard = ({ images, onSave, isLoading }: Props) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [errorFile, setErrorImageFile] = useState(false)
  const [errorTitle, setErrorTitle] = useState('Error al cargar la imagen')

  const [newImageFile, setNewImageFile] = useState<File | null>(null)
  const [newImageStatus, setNewImageStatus] = useState<boolean>(true)
  const [editableImages, setEditableImages] = useState<CarouselItem[]>(images)
  const [isEditing, setIsEditing] = useState(false)
  const [tempUploadedUrl, setTempUploadedUrl] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null)
  const [originalImages, setOriginalImages] = useState<CarouselItem[]>([])

  useEffect(() => {
    setEditableImages(images)
  }, [images])

  const [lightboxToggler, setLightboxToggler] = useState(false)

  const handlePreview = (image_url: string) => {
    setSelectedImage(image_url)
    setLightboxToggler((prev) => !prev)
  }
  const uploadImageBase64 = (file: File) => {
    resetError()

    const reader = new FileReader()

    reader.onloadend = async () => {
      const base64 = reader.result as string

      try {
        setIsUploading(true)

        const response = await apiUrl.post('/configuration/banner/upload', {
          image: base64,
        })

        const uploadedUrl = response.data.url

        setNewImageFile(file)
        setErrorImageFile(false)
        setErrorTitle('')
        setTempUploadedUrl(uploadedUrl)

      } catch (error) {
        console.error('Error al subir la imagen:', error)
        setErrorImageFile(true)
        setErrorTitle('Error al cargar la imagen')
      } finally {
        setIsUploading(false)
      }
    }

    reader.readAsDataURL(file)
  }

  const reassignOrder = (images: CarouselItem[]) => {
    return images.map((item, index) => ({
      ...item,
      order: index + 1,
      id: index + 1,
    }))
  }
  const confirmDelete = (id: number) => {
    customSwal
      .fire({
        icon: 'warning',
        title: '¿Está seguro que desea eliminar esta imagen?',
        reverseButtons: true,
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonColor: '#F9F9F9',
        cancelButtonText: 'No, regresar',

        customClass: {
          cancelButton: 'cancel-button-swal2',
        },
      })
      .then((result) => {
        if (result.isConfirmed) {
          handleDelete(id)
          if (isEditing) {
            customSwal.fire({
              icon: 'success',
              title: 'Imagen eliminada',
              timer: 1500,
            })
          }
        }
      })
  }

  const handleDelete = (id: number) => {
    const filtered = editableImages.filter((item) => item.id !== id)
    const reordered = reassignOrder(filtered)
    setEditableImages(reordered)

    if (!isEditing) {
      saveBannerData(reordered, 'Imagen eliminada con éxito')
    }
  }

  const handleFileChange = async (file: File, index: number) => {
    const reader = new FileReader()

    reader.onloadend = async () => {
      const base64 = reader.result as string

      try {
        setUploadingIndex(index) // Inicia loader en ese index

        const response = await apiUrl.post('/configuration/banner/upload', {
          image: base64,
        })

        const uploadedUrl = response.data.url
        const updated = [...editableImages]
        updated[index].image_url = uploadedUrl
        updated[index].name = file.name
        resetError()
        setEditableImages(updated)
      } catch (error) {
        console.error('Error al subir la imagen editada:', error)
        customSwal.fire({
          icon: 'error',
          title: 'Error al subir la imagen',
          text: 'Inténtalo nuevamente',
        })
      } finally {
        setUploadingIndex(null) // Finaliza loader
      }
    }

    reader.readAsDataURL(file)
  }

  const saveBannerData = async (data: CarouselItem[], message?: string) => {
    try {
      const payload = {
        banner: JSON.stringify({ banner: data }),
      }

      const response = await apiUrl.patch('/configuration/banner', payload, {
        headers: { 'x-api-key': process.env.REACT_APP_PUBLIC_API_KEY },
      })
      const updatedBanner: CarouselItem[] = response.data.data.banner

      setEditableImages(updatedBanner)
      setIsEditing(false)
      setShowModal(false)
      setNewImageFile(null)
      setNewImageStatus(true)
      setTempUploadedUrl(null)
      customSwal.fire({
        icon: 'success',
        title: message ? message : 'Se ha guardado con éxito',
      })
    } catch (error) {
      console.error('Error al guardar el banner:', error)
      customSwal.fire({
        icon: 'error',
        title: 'Error al guardar',
        text: 'Ocurrió un problema al guardar los datos.',
      })
    }
  }

  const handleAddNewImage = () => {
    if (!newImageFile || !tempUploadedUrl) return

    const nextId = editableImages.length > 0 ? Math.max(...editableImages.map((i) => i.id)) + 1 : 1
    const newItem: CarouselItem = {
      id: nextId,
      name: newImageFile.name,
      image_url: tempUploadedUrl,
      visible: newImageStatus,
      order: 0,
    }

    const updated = reassignOrder([...editableImages, newItem])
    setEditableImages(updated)

    if (!isEditing) {
      saveBannerData(updated)
    } else {
      setShowModal(false)
      setNewImageFile(null)
      setNewImageStatus(true)
      setTempUploadedUrl(null)
    }
  }

  const resetError = () => {
    setErrorImageFile(false)
    setErrorTitle('')
  }
  const renderImageCard = (item: CarouselItem, index: number) => (
    <div className='card mt-5 shadow-sm' key={item.id}>
      <div className='p-4'>
        <div className='fw-bold text-dark fs-2 mb-3'>#{item.order}</div>
        <div
          className='upload-box border-dashed rounded p-4 d-flex justify-content-between align-items-center mb-4'
          style={{ border: '2px dashed #7ECAF2' }}
        >
          <div className='d-flex align-items-center'>
            <img
              src={item.image_url}
              alt={item.name}
              width={60}
              height={60}
              className='rounded me-3'
            />
            <div>
              <div className='fw-bold text-dark'>{item.name}</div>
            </div>
          </div>
          {isEditing && (
            <>
              <div className='d-flex align-items-center'>
                <button
                  className='btn mx-4  tx-primary bt-none'
                  onClick={() => confirmDelete(item.id)}
                >
                  <i className='bi bi-trash3 me-2 tx-primary'></i>Eliminar
                </button>
                <label
                  htmlFor={`file-${item.id}`}
                  className={`btn b-outline-secondary ms-3 rounded100 d-flex align-items-center gap-2 ${
                    uploadingIndex === index ? 'disabled' : ''
                  }`}
                  style={{
                    pointerEvents: uploadingIndex === index ? 'none' : 'auto',
                    opacity: uploadingIndex === index ? 0.6 : 1,
                  }}
                >
                  {uploadingIndex === index ? (
                    <>
                      <span
                        className='spinner-border spinner-border-sm'
                        role='status'
                        aria-hidden='true'
                      ></span>
                      Cargando...
                    </>
                  ) : (
                    'Cambiar imagen'
                  )}
                </label>
                <input
                  id={`file-${item.id}`}
                  type='file'
                  accept='image/png, image/jpeg'
                  style={{ display: 'none' }}
                  disabled={uploadingIndex === index}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (!file) return

                    if (!['image/jpeg', 'image/png'].includes(file.type)) {
                      customSwal.fire({
                        icon: 'error',
                        title: 'Formato de archivo no válido',
                        text: 'Solo se permiten archivos PNG o JPG.',
                      })
                      e.currentTarget.value = ''
                      return
                    }

                    if (isDuplicateFileName(file.name, index)) {
                      customSwal.fire({
                        icon: 'error',
                        title: 'Ya existe una imagen con ese nombre',
                        text: 'Por favor, elige otro archivo.',
                      })
                      e.currentTarget.value = ''
                      return
                    }

                    handleFileChange(file, index)
                  }}
                />
              </div>
            </>
          )}
        </div>
        <div className='row'>
          <div className='col-6'>
            <select
              className='form-select'
              value={item.visible ? 'true' : 'false'}
              disabled={!isEditing}
              onChange={(e) => {
                const updated = [...editableImages]
                updated[index].visible = e.target.value === 'true'
                setEditableImages(updated)
              }}
            >
              <option value='true'>Visible</option>
              <option value='false'>Oculto</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  )

  const normalizeName = (name: string) => name.trim().replace(/\s+/g, ' ').toLowerCase()

  const isDuplicateFileName = (name: string, exceptIndex?: number) => {
    const target = normalizeName(name)
    return editableImages.some((img, idx) => {
      if (typeof exceptIndex === 'number' && idx === exceptIndex) return false
      return normalizeName(img.name) === target
    })
  }

  return (
    <div className='container mt-5'>
      <div className='card-body'>
        <div className='card p-5 mb-5'>
          <div className='d-flex justify-content-between align-items-center'>
            <h2 className='text-dark fw-bold'>Listado de imágenes</h2>
            <div>
              {isEditing ? (
                <div className='d-flex justify-content-end gap-2 my-3'>
                  <button
                    className='btn b-outline-secondary'
                    onClick={() => {
                      setIsEditing(false)
                      setEditableImages(originalImages)
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    className='btn b-primary'
                    onClick={() => {
                      saveBannerData(editableImages)
                    }}
                  >
                    Guardar
                  </button>
                </div>
              ) : (
                <>
                  {images && images.length > 0 && (
                    <button
                      className='btn mx-3  tx-primary bt-none'
                      onClick={() => {
                        setIsEditing(true)
                        setOriginalImages(JSON.parse(JSON.stringify(editableImages)))
                      }}
                    >
                      <i className='bi bi-pencil me-2 tx-primary'></i>Editar
                    </button>
                  )}
                  <button
                    className='btn b-primary ml-5'
                    onClick={() => {
                      setShowModal(true)
                    }}
                  >
                    Añadir nueva
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className='d-flex justify-content-center align-items-center py-5'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Cargando...</span>
            </div>
          </div>
        ) : isEditing ? (
          editableImages.map(renderImageCard)
        ) : editableImages && editableImages.length > 0 ? (
          editableImages.map((item) => (
            <div className='card mt-8 ' key={item.id}>
              <div className='d-flex justify-content-between align-items-center p-4 mb-4'>
                <div>
                  <div className='fw-bold text-dark fs-2 mb-1'>#{item.order}</div>
                  <div className='tx-card-file'>Documento: {item.name}</div>
                  <div className='tx-card-file'>Estado: {item.visible ? 'Visible' : 'Oculto'}</div>
                </div>
                <div className='d-flex  align-items-end'>
                  <button
                    className='btn mx-4  tx-primary bt-none'
                    onClick={() => confirmDelete(item.id)}
                  >
                    <i className='bi bi-trash3 me-2 tx-primary'></i>Eliminar
                  </button>
                  <button
                    className='btn b-primary-ligth'
                    onClick={() => handlePreview(item.image_url)}
                  >
                    Previsualizar
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className='card-body d-flex justify-content-center align-items-center bg-white mt-5'>
            <div
              className='card m-5  text-center p-5 w-full'
              style={{ background: '#E2F4FF4D', border: 'none', width: '100%' }}
            >
              <i className='bi bi-images fs-1 tx-card '></i>
              <p className='fw-bold mt-3 tx-card fs-3'>Las imágenes aparecerán en este espacio</p>
              <small className=' tx-card'>Para comenzar, añade una nueva</small>
            </div>
          </div>
        )}

        {isEditing && (
          <div>
            <div className='d-flex justify-content-center m-5'>
              <button className='btn b-primary-ligth' onClick={() => setShowModal(true)}>
                Añadir nueva
              </button>
            </div>
          </div>
        )}

        {showModal && (
          <div className='modal-backdrop-carrousel'>
            <div
              className='modal-content p-5 rounded shadow'
              style={{ background: 'white', maxWidth: 600, margin: 'auto' }}
            >
              <h4 className='mb-4 fw-bold'>Agregar imagen</h4>
              <div
                className='upload-box border-dashed rounded p-4 d-flex justify-content-between align-items-center mb-4'
                style={{ border: '2px dashed #7ECAF2' }}
              >
                {newImageFile ? (
                  <div className='d-flex align-items-center w-100 justify-content-between'>
                    <div className='d-flex align-items-center'>
                      <i className='bi bi-check-circle-fill fs-2 text-success me-3'></i>
                      <div>
                        <div className='fw-bold text-dark'>Archivo cargado</div>
                        <small className='text-muted'>{newImageFile.name}</small>
                      </div>
                    </div>
                    <button
                      className='btn btn-sm b-outline-danger ms-3'
                      onClick={() => setNewImageFile(null)}
                    >
                      Quitar
                    </button>
                  </div>
                ) : (
                  <>
                    <div className='d-flex align-items-center'>
                      <i className='bi bi-image fs-1 me-3' style={{ color: '#007BC3' }}></i>
                      <div>
                        <div className='fw-bold text-dark'>Sube tu imagen</div>
                        <small className='text-muted'>JPG, PNG (Tamaño sugerido 2MB)</small>
                        {errorFile && <div className='text-danger mt-2 d-block'>{errorTitle}</div>}
                      </div>
                    </div>
                    <label
                      htmlFor='image-upload'
                      className={`btn b-outline-secondary rounded100 d-flex align-items-center gap-2 ${
                        isUploading ? 'disabled' : ''
                      }`}
                      style={{
                        pointerEvents: isUploading ? 'none' : 'auto',
                        opacity: isUploading ? 0.6 : 1,
                      }}
                    >
                      {isUploading ? (
                        <>
                          <span
                            className='spinner-border spinner-border-sm'
                            role='status'
                            aria-hidden='true'
                          ></span>
                          Cargando...
                        </>
                      ) : (
                        'Seleccionar archivo'
                      )}
                    </label>
                    <input
                      id='image-upload'
                      type='file'
                      accept='image/png, image/jpeg'
                      style={{ display: 'none' }}
                      disabled={isUploading}
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (!file) return

                        if (!['image/jpeg', 'image/png'].includes(file.type)) {
                          e.currentTarget.value = ''
                          return
                        }

                        if (isDuplicateFileName(file.name)) {
                          setErrorImageFile(true)
                          setErrorTitle('Ya existe un archivo con ese nombre')

                          e.currentTarget.value = ''
                          return
                        }

                        uploadImageBase64(file)
                      }}
                    />
                  </>
                )}
              </div>
              <div className='row'>
                <div className='col-6'>
                  <select
                    className='form-select mb-4'
                    value={newImageStatus ? 'true' : 'false'}
                    onChange={(e) => setNewImageStatus(e.target.value === 'true')}
                  >
                    <option value='true'>Visible</option>
                    <option value='false'>Oculto</option>
                  </select>
                </div>
              </div>
              <div className='d-flex justify-content-end gap-2'>
                <button
                  className='btn b-outline-secondary'
                  onClick={() => {
                    setNewImageFile(null)
                    setShowModal(false)
                    setErrorImageFile(false)
                    setErrorTitle('')
                  }}
                >
                  Cancelar
                </button>
                <button
                  className='btn b-primary'
                  disabled={!newImageFile}
                  onClick={handleAddNewImage}
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        )}

        <div id='carouselImagePreview' style={{ display: 'none' }}></div>
        <FsLightbox toggler={lightboxToggler} sources={[selectedImage]} />
      </div>
    </div>
  )
}
